"use client"

import { Search, Menu, X, ChevronDown, ChevronRight, Users, Briefcase, LogOut } from "lucide-react"
import { useState, useEffect, useRef } from "react"
import { Link, useNavigate } from "react-router-dom"
import { useSelector, useDispatch } from "react-redux"
import logo from "../assets/logo/logo.png"
import { useLanguage } from "../context/LanguageContext"
import { logout } from "../store/slices/authSlice"

// Self-contained, robust LanguageSelector component to prevent ref/state conflicts across multiple instances
const LanguageSelector = ({ compact = false }) => {
  const { currentLanguage, setLanguage, options } = useLanguage()
  const [isOpen, setIsOpen] = useState(false)
  const containerRef = useRef(null)

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false)
      }
    }
    document.addEventListener("click", handleClickOutside)
    return () => document.removeEventListener("click", handleClickOutside)
  }, [])

  const currentOption = options.find((o) => o.code === currentLanguage) || options[0]

  return (
    <div className="relative shrink-0" ref={containerRef}>
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation()
          setIsOpen(!isOpen)
        }}
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        aria-label="Change language"
        className={`flex items-center gap-2 pl-2 pr-2 py-1 rounded-md border transition-all duration-200 font-black uppercase tracking-widest ${
          compact ? "text-[8px] min-w-[4.5rem]" : "text-[9px] min-w-[5.5rem]"
        } justify-between ${
          isOpen
            ? "border-primary bg-primary/5 text-primary shadow-sm"
            : "border-border/60 bg-transparent text-muted-foreground hover:border-primary/50 hover:bg-secondary/50 hover:text-primary"
        }`}
      >
        <span className="flex items-center gap-1.5 shrink-0 min-w-0">
          <img
            src={currentOption?.flag ?? "/France.svg"}
            alt=""
            className={`${compact ? "w-3 h-3" : "w-3.5 h-3.5"} rounded-sm object-cover shrink-0`}
          />
          <span className="truncate">{currentOption?.label ?? currentLanguage}</span>
        </span>
        <ChevronDown
          className={`w-3 h-3 shrink-0 transition-transform duration-200 ${isOpen ? "rotate-180" : ""}`}
          aria-hidden
        />
      </button>

      {isOpen && (
        <div
          role="listbox"
          className="absolute right-0 top-full mt-1.5 bg-card border border-border shadow-lg rounded-md py-0.5 z-[150] min-w-[128px] animate-fade-in"
        >
          {options.map(({ code, label, flag }) => (
            <button
              key={code}
              type="button"
              role="option"
              aria-selected={currentLanguage === code}
              onClick={(e) => {
                e.stopPropagation()
                setIsOpen(false)
                if (code !== currentLanguage) {
                  setLanguage(code)
                }
              }}
              className={`w-full flex items-center gap-2.5 px-3 py-2 text-[9px] font-black uppercase transition-colors rounded ${
                currentLanguage === code ? "bg-primary text-white" : "hover:bg-secondary/80 text-foreground"
              }`}
            >
              <img src={flag} alt="" className="w-4 h-4 rounded-sm object-cover shrink-0" />
              {label}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

const Navbar = () => {
  const primaryNavItems = [
    { name: "Accueil", href: "/" },
    {
      name: "Qui sommes-nous",
      href: "#",
      submenu: [
        { name: "Historique", href: "/historique" },
        { name: "Vision & Mission", href: "/mission" },
        { name: "Valeurs", href: "/valeurs" },
        { name: "Campus", href: "/campus" },
        { name: "Partenariats", href: "/partenariats" },
      ],
    },
    {
      name: "Formation",
      href: "#",
      submenu: [
        {
          name: "Facultés",
          submenu: [
            { name: "DROIT, SCIENCES ECONOMIQUES ET GESTION (FDSEG)", href: "/faculte/fdseg" },
            { name: "LETTRES, LANGUES, COMMUNICATION ET ARTS (FLLCA)", href: "/faculte/fllca" },
            { name: "MEDECINE ET SCIENCES DE LA SANTE", href: "/faculte/medecine" },
            { name: "SCIENCES AGRONOMIQUES ET VETERINAIRES", href: "/faculte/sav" },
            { name: "SCIENCES ET TECHNOLOGIES", href: "/faculte/sciences" },
          ],
        },
        {
          name: "Instituts",
          submenu: [
            { name: "INSTITUT SUPERIEUR DE COMPTABILITE, BANQUE ET ASSURANCE (ISCOBA)", href: "/institut/iscoba" },
            { name: "INSTITUT SUPERIEUR DES SCIENCES AGRONOMIQUES ET VETERINAIRES (ISSAV)", href: "/institut/issav" },
            { name: "INSTITUT SUPERIEUR DES SCIENCES INFORMATIQUES (ISSI)", href: "/institut/issi" },
            { name: "INSTITUTS DE PEDAGOGIE DES LANGUES (IPL), OPTION ANGLAIS", href: "/institut/ipl" },
            { name: "INSTITUT UNIVERSITAIRE DES SCIENCES DE LA SANTÉ (IUSS)", href: "/institut/iuss" },
          ],
        },
        {
          name: "Masters",
          submenu: [
            { name: "NUTRITION HUMAINE ET SECURITE ALIMENTAIRE (FSAV)", href: "/masters/nutrition" },
            { name: "DEVELOPPEMENT RURAL (FSAV)", href: "/masters/developpement-rural" },
            { name: "AGRIBUSINESS (FSAV)", href: "/masters/agribusiness" },
            { name: "DROIT PUBLIC (FDSEG)", href: "/masters/droit-public" },
            { name: "DROIT PRIVÉ (FDSEG)", href: "/masters/droit-prive" },
            { name: "POLITIQUES ÉCONOMIQUES (FDSEG)", href: "/masters/politiques-economiques" },
            { name: "MANAGEMENT DES PROJETS (FDSEG)", href: "/masters/management-projets" },
            { name: "BANQUE & MICROFINANCE (FDSEG)", href: "/masters/banque-microfinance" },
          ],
        },
      ],
    },
    {
      name: "Recherches",
      href: "#",
      submenu: [
        { name: "Centre de Recherche", href: "/recherche/centre" },
        { name: "Publications", href: "/recherche/publications" },
        { name: "Innovation", href: "/actualites?category=innovation" },
        { name: "Académiques", href: "/actualites?category=research" },
      ],
    },
    {
      name: "Espaces Étudiants",
      href: "#",
      submenu: [
        { name: "Calendrier Académique", href: "/etudiants/calendrier" },
        { name: "Admission", href: "/admission" },
        { name: "American Corner Ngozi", href: "/etudiants/american-corner" },
        { name: "IDEAS Box Ngozi", href: "/etudiants/ideax-box" },
        { name: "Blog", href: "/etudiants/blog" },
      ],
    },
    { name: "Education à la paix", href: "/education-paix" },
    { name: "Galerie", href: "/galerie" },
    {
      name: "Actualités & Articles",
      href: "#",
      submenu: [
        { name: "Actualités", href: "/actualites" },
        { name: "Tous les auteurs", href: "/auteurs" },
      ],
    },
  ]

  const secondaryNavItems = [
    {
      name: "Alumni",
      href: "/alumni/events",
      icon: Users,
      highlight: true,
    },
    {
      name: "Équipe Administrative",
      href: "#",
      icon: Briefcase,
      highlight: true,
      submenu: [
        { name: "DIRECTION", href: "/administration/direction" },
        { name: "ADMINISTRATION GENERALE", href: "/administration/general" },
        // { name: "RESSOURCES HUMAINES", href: "/administration/hr" },
        // { name: "FINANCES", href: "/administration/finance" },
      ],
    },
    {
      name: "Étudiants Internationaux",
      href: "/etudiants-internationaux",
      icon: Users,
      highlight: true,
    },
  ]

  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { user, accessToken } = useSelector((s) => s.auth)
  const isLoggedIn = !!(accessToken && user)
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
  const [isScrolled, setIsScrolled] = useState(false)
  const [openMobileMenus, setOpenMobileMenus] = useState({})
  const mobileMenuRef = useRef(null)
  const menuToggleRef = useRef(null)
  const headerRef = useRef(null)
  const scrollLockY = useRef(0)

  const toggleMobileSubmenu = (menuName) => {
    setOpenMobileMenus((prev) => ({
      ...prev,
      [menuName]: !prev[menuName],
    }))
  }

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false)
    setOpenMobileMenus({})
  }

  const handleLogout = () => {
    dispatch(logout())
    navigate("/")
  }

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20)
    }
    window.addEventListener("scroll", handleScroll, { passive: true })
    return () => window.removeEventListener("scroll", handleScroll)
  }, [])

  useEffect(() => {
    if (!isMobileMenuOpen) return
    scrollLockY.current = window.scrollY
    const { style } = document.body
    style.overflow = "hidden"
    style.position = "fixed"
    style.top = `-${scrollLockY.current}px`
    style.width = "100%"

    return () => {
      style.overflow = ""
      style.position = ""
      style.top = ""
      style.width = ""
      window.scrollTo(0, scrollLockY.current)
    }
  }, [isMobileMenuOpen])

  // Close mobile menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        isMobileMenuOpen &&
        mobileMenuRef.current &&
        !mobileMenuRef.current.contains(event.target) &&
        menuToggleRef.current &&
        !menuToggleRef.current.contains(event.target)
      ) {
        closeMobileMenu()
      }
    }

    document.addEventListener("click", handleClickOutside)
    return () => document.removeEventListener("click", handleClickOutside)
  }, [isMobileMenuOpen])

  const renderMenuItem = (item, isSecondary = false, index = 0, total = 0) => {
    const isRightAligned = isSecondary || (!isSecondary && index >= total - 2)

    return (
      <div key={item.name} className="relative group">
        {item.href === "#" ? (
          <button
            className={`px-1.5 xl:px-2 2xl:px-3 py-2 text-[9px] xl:text-[10px] 2xl:text-[11px] font-bold transition-smooth flex items-center gap-1 xl:gap-1.5 whitespace-nowrap relative group/item ${isSecondary
              ? item.highlight
                ? "text-accent bg-accent/5 hover:bg-accent/10 rounded-sm"
                : "text-muted-foreground/80 hover:text-primary"
              : "text-foreground/90 hover:text-primary"
              }`}
            aria-haspopup="true"
          >
            {item.icon && <item.icon className="w-3 xl:w-3.5 h-3 xl:h-3.5" />}
            {item.name}
            {item.submenu && <ChevronDown className="w-2.5 xl:w-3 h-2.5 xl:h-3 opacity-50 group-hover:rotate-180 transition-transform duration-200" />}
            {!isSecondary && <span className="absolute left-2 right-2 bottom-0 h-0.5 bg-accent scale-x-0 group-hover/item:scale-x-100 transition-transform origin-left duration-300"></span>}
          </button>
        ) : (
          <Link
            to={item.href}
            className={`px-1.5 xl:px-2 2xl:px-3 py-2 text-[9px] xl:text-[10px] 2xl:text-[11px] font-bold transition-smooth flex items-center gap-1 xl:gap-1.5 whitespace-nowrap relative group/item ${isSecondary
              ? item.highlight
                ? "text-accent bg-accent/5 hover:bg-accent/10 rounded-sm"
                : "text-muted-foreground/80 hover:text-primary"
              : "text-foreground/90 hover:text-primary"
              }`}
          >
            {item.icon && <item.icon className="w-3 xl:w-3.5 h-3 xl:h-3.5" />}
            {item.name}
            {!isSecondary && <span className="absolute left-2 right-2 bottom-0 h-0.5 bg-accent scale-x-0 group-hover/item:scale-x-100 transition-transform origin-left duration-300"></span>}
          </Link>
        )}

        {item.submenu && (
          <div
            className={`absolute ${isRightAligned ? "right-0" : "left-0"} top-full pt-1 hidden group-hover:block z-[100]`}
          >
            <div className="bg-white dark:bg-card border border-border shadow-2xl rounded-sm min-w-[280px] backdrop-blur-xl">
              <div className="px-4 py-2.5 bg-primary/5 border-b border-border">
                <span className="text-[9px] font-black uppercase tracking-[0.2em] text-primary">{item.name}</span>
              </div>
              <div className="py-1">
                {item.submenu.map((subItem) => (
                  <div key={subItem.name} className="relative group/sub">
                    {subItem.submenu ? (
                      <div className="relative">
                        <div className="flex items-center justify-between px-4 py-2.5 text-xs text-foreground/80 hover:bg-accent/5 hover:text-primary transition-smooth cursor-default font-bold">
                          <span>{subItem.name}</span>
                          <ChevronRight className="w-3.5 h-3.5 ml-4 opacity-50 transition-transform group-hover/sub:translate-x-1" />
                        </div>
                        <div className="absolute left-full top-0 pl-1 hidden group-hover/sub:block min-w-[320px] z-[110]">
                          <div className="bg-white dark:bg-card border border-border shadow-2xl rounded-sm py-1 backdrop-blur-xl">
                            {subItem.submenu.map((nested) => (
                              <Link
                                key={nested.name}
                                to={nested.href}
                                className="block px-4 py-2 text-[10px] text-foreground/80 hover:bg-accent/5 hover:text-primary transition-smooth font-bold"
                              >
                                {nested.name}
                              </Link>
                            ))}
                          </div>
                        </div>
                      </div>
                    ) : (
                      <Link
                        to={subItem.href}
                        className="block px-4 py-2.5 text-xs text-foreground/80 hover:bg-accent/5 hover:text-primary transition-smooth font-bold"
                      >
                        {subItem.name}
                      </Link>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    )
  }

  return (
    <>
      <header
        ref={headerRef}
        className={`fixed top-0 inset-x-0 w-full z-50 border-b border-border/40 transition-[background,box-shadow] duration-300 ease-out ${isScrolled ? "bg-background/95 backdrop-blur-md shadow-md" : "bg-background"
          }`}
        style={{ maxWidth: '100vw' }}
      >
        {isLoggedIn && (
          <div className="bg-primary text-primary-foreground py-1.5 px-4 flex justify-center items-center gap-2 text-[10px] sm:text-xs flex-wrap border-b border-white/10">
            <span>Connecté en tant que {user?.fullname || user?.email}</span>
            <div className="flex items-center gap-2">
              <Link
                to="/dashboard"
                className="px-2 py-0.5 bg-white/20 hover:bg-white/30 rounded text-[9px] sm:text-[10px] font-bold transition-colors uppercase tracking-wider"
              >
                Tableau de bord
              </Link>
              <button
                type="button"
                onClick={handleLogout}
                className="px-2 py-0.5 bg-white/20 hover:bg-white/30 rounded text-[9px] sm:text-[10px] font-bold transition-colors inline-flex items-center gap-1 uppercase tracking-wider"
              >
                <LogOut className="w-3 h-3" />
                Déconnexion
              </button>
            </div>
          </div>
        )}
        {/* Utility bar - language + search on desktop; collapses when scrolled */}
        <div
          className={`hidden lg:block transition-[max-height,opacity] duration-300 relative z-[60] ${isScrolled ? "max-h-0 opacity-0 pointer-events-none" : "max-h-10 opacity-100"
            }`}
        >
          <div className="container h-9 flex items-center justify-end gap-3 px-4 text-[9px] text-muted-foreground bg-secondary/30 backdrop-blur-sm border-b border-border/5">
            <LanguageSelector compact={false} />
            <button
              type="button"
              onClick={() => navigate("/actualites")}
              className="flex items-center gap-1.5 hover:text-primary transition-smooth font-black uppercase tracking-widest px-1.5"
              aria-label="Rechercher dans les actualités"
            >
              <Search className="w-2.5 h-2.5 shrink-0" />
              <span>Rechercher</span>
            </button>
          </div>
        </div>

        {/* Main Navbar */}
        <div className={`transition-[padding] duration-300 ease-out ${isScrolled ? "py-1.5" : "py-3"}`}>
          <div className="container flex items-center justify-between gap-2 sm:gap-3 min-w-0">
            <Link
              to="/"
              className="flex items-center gap-2 sm:gap-3 group min-w-0 flex-1 lg:flex-initial max-w-[calc(100%-7rem)] lg:max-w-none"
              aria-label="Accueil"
            >
              <div
                className={`transition-all duration-300 overflow-hidden shadow-sm group-hover:shadow-md shrink-0 ${isScrolled ? "w-8 h-8" : "w-10 h-10"
                  }`}
              >
                <img src={logo} alt="Logo UNG" className="w-full h-full object-contain" />
              </div>
              <div className="transition-smooth min-w-0 hidden sm:block -translate-y-[1px]">
                <h1
                  className={`font-black text-foreground group-hover:text-primary transition-all leading-none tracking-tight truncate ${isScrolled ? "text-xs" : "text-sm"
                    }`}
                >
                  Université de Ngozi
                </h1>
                <p className="text-[8px] text-muted-foreground font-bold uppercase tracking-[0.2em] mt-0.5 opacity-60 truncate">
                  Paix • Savoir • Développement
                </p>
              </div>
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center gap-0 2xl:gap-1 min-w-0 flex-1 justify-center">
              {primaryNavItems.map((item, index) => renderMenuItem(item, false, index, primaryNavItems.length))}
            </nav>

            <div className="flex items-center gap-1.5 sm:gap-2 shrink-0">
              <div className={isScrolled ? "flex" : "flex lg:hidden"}>
                <LanguageSelector compact={isScrolled} />
              </div>
              <button
                type="button"
                onClick={() => navigate("/actualites")}
                className="lg:hidden p-2 hover:bg-secondary rounded-sm transition-all"
                aria-label="Rechercher dans les actualités"
              >
                <Search className="w-4 h-4" />
              </button>
              <button
                ref={menuToggleRef}
                type="button"
                onClick={() => {
                  setIsMobileMenuOpen((open) => !open)
                }}
                className="lg:hidden p-2 hover:bg-secondary rounded-sm transition-all active:scale-90"
                aria-label="Toggle menu"
                aria-expanded={isMobileMenuOpen}
              >
                {isMobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
              </button>
            </div>
          </div>
        </div>

        {/* Secondary items bar - Compact & Sticky beneath main nav */}
        <div className={`hidden lg:block transition-all duration-300 border-t border-border/10 bg-gradient-to-r from-accent/5 via-primary/5 to-accent/5 ${isScrolled ? "h-8" : "h-10"}`}>
          <div className="container h-full flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="h-4 w-1 bg-accent rounded-full"></div>
              <span className="text-[9px] font-black uppercase tracking-[0.2em] text-muted-foreground/60">Portail Académique</span>
            </div>
            <div className="flex items-center gap-1">
              {secondaryNavItems.map((item, index) => renderMenuItem(item, true, index, secondaryNavItems.length))}
            </div>
          </div>
        </div>

      </header>

      {/* Mobile Menu — fixed shell; only inner panel scrolls */}
      {isMobileMenuOpen && (
        <div
          ref={mobileMenuRef}
          className="lg:hidden fixed inset-0 z-[100] bg-background flex flex-col h-[100dvh] max-h-[100dvh] overflow-hidden animate-fade-in"
        >
          <div className="shrink-0 border-b border-border bg-background">
            <div className="container py-4 flex justify-between items-center gap-3">
              <div className="flex items-center gap-3 min-w-0">
                <img src={logo} alt="Logo" className="w-8 h-8 object-contain shrink-0" />
                <span className="font-black text-xs uppercase tracking-widest text-primary truncate">Menu UNG</span>
              </div>
              <button type="button" onClick={closeMobileMenu} className="p-2 bg-secondary rounded-full shrink-0" aria-label="Fermer le menu">
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>

          <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain">
            <div className="container py-6 space-y-6">
              {/* Responsive Language Selector */}
              <div className="flex items-center justify-between pb-4 border-b border-border/30">
                <span className="text-[10px] font-black uppercase tracking-widest text-muted-foreground">Langue / Language</span>
                <LanguageSelector compact={false} />
              </div>
              <nav className="grid gap-1">
                {primaryNavItems.map((item) => (
                  <div key={item.name} className="border-b border-border/30 last:border-0">
                    {item.submenu ? (
                      <div className="py-1">
                        <button
                          onClick={() => toggleMobileSubmenu(item.name)}
                          className="w-full flex items-center justify-between py-3 text-xs font-black uppercase tracking-widest text-foreground/80 hover:text-primary transition-colors"
                        >
                          {item.name}
                          <ChevronDown className={`w-3.5 h-3.5 transition-transform duration-300 ${openMobileMenus[item.name] ? "rotate-180" : ""}`} />
                        </button>
                        {openMobileMenus[item.name] && (
                          <div className="pl-4 py-1 grid gap-1 border-l-2 border-accent/20 mb-2">
                            {item.submenu.map(sub => (
                              <div key={sub.name}>
                                {sub.submenu ? (
                                  <div className="py-1">
                                    <button
                                      onClick={() => toggleMobileSubmenu(`${item.name}-${sub.name}`)}
                                      className="w-full flex items-center justify-between py-2 text-[11px] font-bold text-muted-foreground/80 hover:text-primary transition-colors"
                                    >
                                      {sub.name}
                                      <ChevronDown className={`w-3 h-3 transition-transform duration-300 ${openMobileMenus[`${item.name}-${sub.name}`] ? "rotate-180" : ""}`} />
                                    </button>
                                    {openMobileMenus[`${item.name}-${sub.name}`] && (
                                      <div className="pl-3 py-1 grid gap-1 border-l border-border/50 ml-1">
                                        {sub.submenu.map(nested => (
                                          <Link
                                            key={nested.name}
                                            to={nested.href}
                                            onClick={closeMobileMenu}
                                            className="block py-2 text-[10px] font-bold text-muted-foreground/60 hover:text-primary transition-colors"
                                          >
                                            {nested.name}
                                          </Link>
                                        ))}
                                      </div>
                                    )}
                                  </div>
                                ) : (
                                  <Link
                                    to={sub.href}
                                    onClick={closeMobileMenu}
                                    className="block py-2 text-[11px] font-bold text-muted-foreground/80 hover:text-primary transition-colors"
                                  >
                                    {sub.name}
                                  </Link>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    ) : (
                      <Link
                        to={item.href}
                        onClick={closeMobileMenu}
                        className="block py-4 text-xs font-black uppercase tracking-widest text-foreground/80 hover:text-primary transition-colors"
                      >
                        {item.name}
                      </Link>
                    )}
                  </div>
                ))}
              </nav>

              <div className="pt-8 grid gap-4">
                {secondaryNavItems.map(item => (
                  <div key={item.name}>
                    {item.submenu ? (
                      <div className="grid gap-1">
                        <button
                          onClick={() => toggleMobileSubmenu(item.name)}
                          className="flex items-center justify-between w-full p-4 bg-primary text-white rounded-sm font-black text-[10px] uppercase tracking-widest"
                        >
                          <div className="flex items-center gap-3">
                            {item.icon && <item.icon className="w-4 h-4" />}
                            {item.name}
                          </div>
                          <ChevronDown className={`w-4 h-4 transition-transform duration-300 ${openMobileMenus[item.name] ? "rotate-180" : ""}`} />
                        </button>
                        {openMobileMenus[item.name] && (
                          <div className="grid gap-1 mt-1">
                            {item.submenu.map(sub => (
                              <Link
                                key={sub.name}
                                to={sub.href}
                                onClick={closeMobileMenu}
                                className="flex items-center gap-3 p-3 bg-secondary/50 text-foreground rounded-sm font-bold text-[10px] uppercase tracking-widest pl-11"
                              >
                                {sub.name}
                              </Link>
                            ))}
                          </div>
                        )}
                      </div>
                    ) : (
                      <Link key={item.name} to={item.href} onClick={closeMobileMenu} className="flex items-center gap-3 p-4 bg-primary text-white rounded-sm font-black text-[10px] uppercase tracking-widest">
                        {item.icon && <item.icon className="w-4 h-4" />}
                        {item.name}
                      </Link>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  )
}

export default Navbar
